import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { innerColor, themeColor, modernDesignSystem } from '../constants/themes';
import axios from 'axios';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Stack,
  Typography,
  Paper,
  Box,
  Chip,
} from '@mui/material';
import { 
  LocationOn, 
  Search, 
  Clear,
  Home,
  Apartment,
  LocationCity
} from '@mui/icons-material';

import {
  Sanliurfa,
  Akcakale,
  Birecik,
  Bozova,
  Ceylanpinar,
  Eyyubiye,
  Halfeti,
  Haliliye,
  Harran,
  Hilvan,
  Karakopru,
  Siverek,
  Suruc,
  Viransehir,
} from '../constants/geojson.files';

import {
  geojsonToLayer,
  geojsonParsetoText,
  zoomToLayer,
  addLayerToMap,
  removeKadastralLayer,
  parcelGeojsonToLayer,
  normalizeTurkishText,
} from '../utils/geojson.tools';

import { queryParcelURL, queryVillagesURL } from '../api';

const villageNameAliases = {
  Hilvan: {
    Yakınyurt: 'Yakinyurt',
  },
  Karaköprü: {
    Çakmak: 'Cakmak',
    Yığınak: 'Yiğinak',
  },
};

const getGeojsonVillageNames = (geojsonObject) =>
  (geojsonObject?.features || [])
    .map((feature) => feature.properties?.text)
    .filter(Boolean);

const resolveGeojsonVillageName = (dbVillageName, districtName, geojsonVillageNames) => {
  const aliasName = villageNameAliases[districtName]?.[dbVillageName] || dbVillageName;
  const exactMatch = geojsonVillageNames.find((name) => name === aliasName);

  if (exactMatch) {
    return exactMatch;
  }

  const normalizedAliasName = normalizeTurkishText(aliasName);
  return geojsonVillageNames.find(
    (name) => normalizeTurkishText(name) === normalizedAliasName
  );
};

const MyComponent = () => {
  const { map } = useSelector((state) => state.globalMaps[0]);

  const [il, setIl] = useState('');
  const [ilce, setIlce] = useState('');
  const [mahalle, setMahalle] = useState('');
  const [mahalleId, setMahalleId] = useState('');
  const [ada, setAda] = useState('');
  const [parsel, setParsel] = useState('');

  const [ilceler, setIlceler] = useState([]);
  const [dbMahalleler, setDbMahalleler] = useState([]);

  const [ilGeoJson, setIlGeoJson] = useState(null);
  const [ilceGeoJson, setIlceGeoJson] = useState(null);
  const [parselGeoJson, setParselGeoJson] = useState(null);

  const handleIlChange = (event) => {
    const il = event.target.value;
    setIl(il);
    setIlce('');
    setMahalle('');
  };

  const handleIlceChange = (event) => {
    const ilce = event.target.value;
    setIlce(ilce);
    setMahalle('');
  };

  const handleMahalleChange = (event) => {
    const mahalle = event.target.value;
    setMahalle(mahalle);
  };

  const fetchGeoJson = async (district, type) => {
    try {
      const response = await fetch(district);
      const data = await response.text();
      if (type === 'il') {
        setIlGeoJson(JSON.parse(data));
      } else if (type === 'ilce') {
        setIlceGeoJson(JSON.parse(data));
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (il) {
      fetchGeoJson(Sanliurfa, 'il');
    }
  }, [il]);

  useEffect(() => {
    if (ilGeoJson) {
      const ilceler = geojsonParsetoText(ilGeoJson);
      setIlceler(ilceler);
    }
  }, [ilGeoJson]);

  useEffect(() => {
    if (!il || !ilce) {
      setDbMahalleler([]);
      return;
    }

    let isCancelled = false;

    const fetchVillages = async () => {
      try {
        const { data } = await axios.get(queryVillagesURL, {
          params: { il, ilce },
        });
        if (!isCancelled) {
          setDbMahalleler(data.koyler || []);
        }
      } catch (error) {
        console.error('Köy listesi alınırken hata oluştu:', error);
        if (!isCancelled) {
          setDbMahalleler([]);
        }
      }
    };

    fetchVillages();

    return () => {
      isCancelled = true;
    };
  }, [il, ilce]);

  const mahalleler = useMemo(() => {
    if (!ilceGeoJson) {
      return dbMahalleler;
    }

    const geojsonVillageNames = getGeojsonVillageNames(ilceGeoJson);
    const optionMap = new Map();

    dbMahalleler.forEach((dbVillageName) => {
      const geojsonVillageName = resolveGeojsonVillageName(
        dbVillageName,
        ilce,
        geojsonVillageNames
      );

      // DB'deki her köy mutlaka listede yer almalı: geojson'da bir sınırı
      // varsa o ismi (zoom için) kullan, yoksa doğrudan DB'deki ismiyle göster.
      const displayName = geojsonVillageName || dbVillageName;
      const optionKey = normalizeTurkishText(displayName);
      if (!optionMap.has(optionKey)) {
        optionMap.set(optionKey, displayName);
      }
    });

    return Array.from(optionMap.values()).sort((a, b) =>
      a.localeCompare(b, 'tr', { sensitivity: 'base' })
    );
  }, [dbMahalleler, ilce, ilceGeoJson]);

  const ilceToMap = useCallback(
    (ilcePath, ilGeojson, ilceString) => {
      fetchGeoJson(ilcePath, 'ilce');
      const { vectorLayer } = geojsonToLayer(ilGeojson, ilceString);
      addLayerToMap(vectorLayer, map);
      zoomToLayer(map, vectorLayer);
    },
    [map]
  );

  useEffect(() => {
    switch (ilce) {
      case 'Akçakale':
        ilceToMap(Akcakale, ilGeoJson, 'Akçakale');
        break;
      case 'Birecik':
        ilceToMap(Birecik, ilGeoJson, 'Birecik');
        break;
      case 'Bozova':
        ilceToMap(Bozova, ilGeoJson, 'Bozova');
        break;
      case 'Ceylanpinar':
        ilceToMap(Ceylanpinar, ilGeoJson, 'Ceylanpinar');
        break;
      case 'Eyyübiye':
        ilceToMap(Eyyubiye, ilGeoJson, 'Eyyübiye');
        break;
      case 'Halfeti':
        ilceToMap(Halfeti, ilGeoJson, 'Halfeti');
        break;
      case 'Haliliye':
        ilceToMap(Haliliye, ilGeoJson, 'Haliliye');
        break;
      case 'Harran':
        ilceToMap(Harran, ilGeoJson, 'Harran');
        break;
      case 'Hilvan':
        ilceToMap(Hilvan, ilGeoJson, 'Hilvan');
        break;
      case 'Karaköprü':
        ilceToMap(Karakopru, ilGeoJson, 'Karaköprü');
        break;
      case 'Siverek':
        ilceToMap(Siverek, ilGeoJson, 'Siverek');
        break;
      case 'Suruç':
        ilceToMap(Suruc, ilGeoJson, 'Suruç');
        break;
      case 'Viranşehir':
        ilceToMap(Viransehir, ilGeoJson, 'Viranşehir');
        break;
      default:
        break;
    }
  }, [map, ilce, ilGeoJson, ilceToMap]);

  useEffect(() => {
    if (mahalle && ilceGeoJson) {
      const { vectorLayer, geometryId } = geojsonToLayer(ilceGeoJson, mahalle);
      setMahalleId(geometryId);
      if (vectorLayer) {
        addLayerToMap(vectorLayer, map);
        zoomToLayer(map, vectorLayer);
      }
    }
  }, [ilceGeoJson, mahalle, map]);

  useEffect(() => {
    if (parselGeoJson) {
      const vectorLayer = parcelGeojsonToLayer(parselGeoJson);
      addLayerToMap(vectorLayer, map);
      zoomToLayer(map, vectorLayer);
    } else {
      removeKadastralLayer(map);
    }
  }, [parselGeoJson, map]);

/*     useEffect(() => {
    console.log(il, ilce, mahalle);
  }, [il, ilce, mahalle]); */

  const parcelQuery = async () => {
    try {
      const { data } = await axios.post(queryParcelURL, {
        il,
        ilce,
        koy: mahalle,
        ada,
        parsel,
      });
      setParselGeoJson(data);
      toast.success('Aradığınız parsel bulundu', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'dark',
      });
    } catch (error) {
      toast.error('Aradığınız kriterlere uygun parsel bulunamadı!', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'dark',
      });
    }
  };

  return (
    <Box sx={{ padding: modernDesignSystem.spacing.xs }}>
      {/* Kompakt Başlık Bölümü */}
      <Paper
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${modernDesignSystem.colors.primary.surface} 0%, ${modernDesignSystem.colors.neutral.gray50} 100%)`,
          padding: modernDesignSystem.spacing.sm,
          borderRadius: modernDesignSystem.borders.radius.small,
          marginBottom: modernDesignSystem.spacing.sm,
          border: `1px solid ${modernDesignSystem.colors.primary.light}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: 0.5 }}>
          <Home sx={{ color: modernDesignSystem.colors.primary.main, fontSize: '1.1rem' }} />
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: modernDesignSystem.colors.primary.dark,
              fontSize: modernDesignSystem.typography.fontSize.base,
            }}
          >
            Parsel Arama
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: modernDesignSystem.colors.neutral.gray600,
            fontSize: modernDesignSystem.typography.fontSize.xs,
          }}
        >
          İl, ilçe, mahalle bilgileri ile parsel arayabilirsiniz
        </Typography>
      </Paper>

      <Stack spacing={modernDesignSystem.spacing.sm}>
        {/* Kompakt Form Elementleri */}
        <FormControl fullWidth size="small">
          <InputLabel 
            htmlFor="il"
            sx={{
              color: modernDesignSystem.colors.primary.main,
              '&.Mui-focused': {
                color: modernDesignSystem.colors.primary.dark,
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationCity fontSize="small" />
              İl
            </Box>
          </InputLabel>
          <Select
            labelId="il"
            id="il"
            label="İl"
            value={il}
            onChange={handleIlChange}
            fullWidth
            sx={{
              backgroundColor: !il ? modernDesignSystem.colors.primary.surface : modernDesignSystem.colors.neutral.white,
              borderRadius: modernDesignSystem.borders.radius.small,
              '& .MuiOutlinedInput-root': {
                borderRadius: modernDesignSystem.borders.radius.small,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: modernDesignSystem.colors.primary.light,
                borderWidth: '2px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: modernDesignSystem.colors.primary.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: modernDesignSystem.colors.primary.dark,
              }
            }}
          >
            <MenuItem value="Sanliurfa">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn fontSize="small" sx={{ color: modernDesignSystem.colors.primary.main }} />
                Şanlıurfa
              </Box>
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel 
            htmlFor="ilce"
            sx={{
              color: modernDesignSystem.colors.primary.main,
              '&.Mui-focused': {
                color: modernDesignSystem.colors.primary.dark,
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Apartment fontSize="small" />
              İlçe
            </Box>
          </InputLabel>
          <Select
            labelId="ilce"
            id="ilce"
            label="İlçe"
            value={ilce}
            onChange={handleIlceChange}
            fullWidth
            disabled={!il}
            sx={{
              backgroundColor: !il ? modernDesignSystem.colors.neutral.gray100 : 
                               !ilce ? modernDesignSystem.colors.primary.surface : 
                               modernDesignSystem.colors.neutral.white,
              borderRadius: modernDesignSystem.borders.radius.small,
              '& .MuiOutlinedInput-root': {
                borderRadius: modernDesignSystem.borders.radius.small,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: !il ? modernDesignSystem.colors.neutral.gray300 : modernDesignSystem.colors.primary.light,
                borderWidth: '2px',
              }
            }}
          >
            {ilceler.map((ilce, index) => (
              <MenuItem key={index} value={ilce}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn fontSize="small" sx={{ color: modernDesignSystem.colors.primary.main }} />
                  {ilce}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel 
            htmlFor="mahalle"
            sx={{
              color: modernDesignSystem.colors.primary.main,
              '&.Mui-focused': {
                color: modernDesignSystem.colors.primary.dark,
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Home fontSize="small" />
              Mahalle
            </Box>
          </InputLabel>
          <Select
            labelId="mahalle"
            id="mahalle"
            label="Mahalle"
            value={mahalle}
            onChange={handleMahalleChange}
            fullWidth
            disabled={!ilce}
            sx={{
              backgroundColor: !ilce ? modernDesignSystem.colors.neutral.gray100 : 
                               !mahalle ? modernDesignSystem.colors.primary.surface : 
                               modernDesignSystem.colors.neutral.white,
              borderRadius: modernDesignSystem.borders.radius.small,
              '& .MuiOutlinedInput-root': {
                borderRadius: modernDesignSystem.borders.radius.small,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: !ilce ? modernDesignSystem.colors.neutral.gray300 : modernDesignSystem.colors.primary.light,
                borderWidth: '2px',
              }
            }}
          >
            {mahalleler.map((mahalle, index) => (
              <MenuItem key={index} value={mahalle}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn fontSize="small" sx={{ color: modernDesignSystem.colors.primary.main }} />
                  {mahalle}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Kompakt Ada ve Parsel */}
        <Paper
          elevation={0}
          sx={{
            padding: modernDesignSystem.spacing.sm,
            backgroundColor: modernDesignSystem.colors.neutral.gray50,
            borderRadius: modernDesignSystem.borders.radius.small,
            border: `1px solid ${modernDesignSystem.colors.neutral.gray200}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              marginBottom: modernDesignSystem.spacing.xs,
              color: modernDesignSystem.colors.neutral.gray700,
              fontWeight: 600,
              display: 'block',
            }}
          >
            Parsel Detayları
          </Typography>
          
          <Stack spacing={modernDesignSystem.spacing.sm}>
            <TextField
              type="number"
              id="ada"
              label="Ada"
              variant="outlined"
              size="small"
              value={ada}
              onChange={(e) => setAda(e.target.value)}
              required
              disabled={!mahalle}
              fullWidth
              sx={{
                backgroundColor: !mahalle ? modernDesignSystem.colors.neutral.gray100 : modernDesignSystem.colors.neutral.white,
                '& .MuiOutlinedInput-root': {
                  borderRadius: modernDesignSystem.borders.radius.small,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: !mahalle ? modernDesignSystem.colors.neutral.gray300 : modernDesignSystem.colors.primary.light,
                  borderWidth: '2px',
                }
              }}
              inputProps={{
                min: 1,
                step: 1,
              }}
            />
            
            <TextField
              type="number"
              id="parsel"
              label="Parsel"
              variant="outlined"
              size="small"
              value={parsel}
              onChange={(e) => setParsel(e.target.value)}
              required
              disabled={!ada}
              fullWidth
              sx={{
                backgroundColor: !ada ? modernDesignSystem.colors.neutral.gray100 : modernDesignSystem.colors.neutral.white,
                '& .MuiOutlinedInput-root': {
                  borderRadius: modernDesignSystem.borders.radius.small,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: !ada ? modernDesignSystem.colors.neutral.gray300 : modernDesignSystem.colors.primary.light,
                  borderWidth: '2px',
                }
              }}
              inputProps={{
                min: 1,
                step: 1,
              }}
            />
          </Stack>
        </Paper>

        {/* Kompakt Butonlar */}
        <Stack spacing={modernDesignSystem.spacing.xs}>
          <Button
            onClick={parcelQuery}
            variant="contained"
            fullWidth
            disabled={!parsel}
            startIcon={<Search />}
            size="small"
            sx={{
              background: `linear-gradient(135deg, ${modernDesignSystem.colors.primary.main} 0%, ${modernDesignSystem.colors.primary.dark} 100%)`,
              color: modernDesignSystem.colors.neutral.white,
              borderRadius: modernDesignSystem.borders.radius.small,
              padding: `${modernDesignSystem.spacing.sm} ${modernDesignSystem.spacing.md}`,
              fontSize: modernDesignSystem.typography.fontSize.sm,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: modernDesignSystem.shadows.medium,
              
              '&:hover:not(:disabled)': {
                background: `linear-gradient(135deg, ${modernDesignSystem.colors.primary.dark} 0%, ${modernDesignSystem.colors.secondary.dark} 100%)`,
                boxShadow: modernDesignSystem.shadows.elevated,
                transform: 'translateY(-1px)',
              }
            }}
          >
            Parsel Sorgula
          </Button>

          {(il || ilce || mahalle || ada || parsel) && (
            <Button
              onClick={() => {
                setIl('');
                setIlce('');
                setMahalle('');
                setAda('');
                setParsel('');
                setParselGeoJson(null);
                removeKadastralLayer(map);
              }}
              variant="outlined"
              fullWidth
              startIcon={<Clear />}
              size="small"
              sx={{
                borderColor: modernDesignSystem.colors.secondary.main,
                color: modernDesignSystem.colors.secondary.main,
                borderRadius: modernDesignSystem.borders.radius.small,
                fontSize: modernDesignSystem.typography.fontSize.xs,
                fontWeight: 500,
                textTransform: 'none',
                borderWidth: '2px',
                
                '&:hover': {
                  borderColor: modernDesignSystem.colors.secondary.dark,
                  backgroundColor: modernDesignSystem.colors.secondary.surface,
                  color: modernDesignSystem.colors.secondary.dark,
                  borderWidth: '2px',
                }
              }}
            >
              Temizle
            </Button>
          )}
        </Stack>

        {/* Kompakt Durum Göstergesi */}
        {il && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Chip
              label={il}
              size="small"
              sx={{
                backgroundColor: modernDesignSystem.colors.primary.surface,
                color: modernDesignSystem.colors.primary.dark,
                fontWeight: 500,
                height: '24px',
                fontSize: '0.7rem',
              }}
            />
            {ilce && (
              <Chip
                label={ilce}
                size="small"
                sx={{
                  backgroundColor: modernDesignSystem.colors.primary.surface,
                  color: modernDesignSystem.colors.primary.dark,
                  fontWeight: 500,
                  height: '24px',
                  fontSize: '0.7rem',
                }}
              />
            )}
            {mahalle && (
              <Chip
                label={mahalle}
                size="small"
                sx={{
                  backgroundColor: modernDesignSystem.colors.primary.surface,
                  color: modernDesignSystem.colors.primary.dark,
                  fontWeight: 500,
                  height: '24px',
                  fontSize: '0.7rem',
                }}
              />
            )}
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default MyComponent;
